import React, { useRef, useState } from "react";
import PropTypes from "prop-types";

// antd
import {
  Typography as Typography1,
  Tabs,
  Badge,
  Dropdown,
  List,
  Avatar,
  Space,
  Button,
  Tooltip,
  Divider,
  theme as antTheme
} from "antd";
import { MailOutlined } from "@ant-design/icons";

import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router";
// project import
import { defaultColors } from '@/themes/antd-theme';
import {
  bp_get_batch_notification_for_employee,
  get_api_function,
  get_ingredient_value_alert,
} from '@/utils/api';
import { useTheme } from '@/App';
import { formatDate, formatDateTime } from '@/utils/datetime';
import useLiveNotifications from '@/hooks/useLiveNotifications';

// Icon alias for compatibility
const EmailOutlinedIcon = MailOutlined;

// Component aliases for compatibility
const Stack = Space;

const deviation_color = (per) => {
  let color;
  let bg_color;
  color = "green";
  bg_color = "#CCFFCC ";
  return { color: color, bg_color: bg_color };
};

// ==============================|| HEADER CONTENT - NOTIFICATION ||============================== //

function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`profile-tabpanel-${index}`}
      aria-labelledby={`profile-tab-${index}`}
      {...other}
    >
      {value === index && children}
    </div>
  );
}

TabPanel.propTypes = {
  children: PropTypes.node,
  index: PropTypes.any.isRequired,
  value: PropTypes.any.isRequired,
};

const ScheduleNotification = () => {
  const { isDark } = useTheme();
  const { token } = antTheme.useToken();
  const navigate = useNavigate();

  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);
  const [notificationsData, setNotificationsData] = useState([]);

  useLiveNotifications(() => {
    get_api_function(
      "/get_schedule_notification",
      (res) => {
        setNotificationsData(res?.data?.data || []);
      },
      undefined  // No params needed for this endpoint
    );
  }, ['schedule_notification'], 60000);

  const handleToggle = () => {
    setOpen((prevOpen) => !prevOpen);
  };

  const handleClose = () => {
    setOpen(false);
  };

  // Dropdown menu content
  const dropdownContent = (
    <div style={{
      width: 420,
      maxWidth: '100vw',
      backgroundColor: token.colorBgElevated,
      borderRadius: token.borderRadiusLG,
      boxShadow: token.boxShadowSecondary,
    }}>
      <List
        itemLayout="horizontal"
        dataSource={notificationsData.slice(0, 5)}
        renderItem={(item, index) => (
          <React.Fragment key={index}>
            <List.Item
              style={{
                padding: '12px 16px',
                cursor: 'pointer'
              }}
            >
              <List.Item.Meta
                avatar={
                  <Avatar
                    style={{
                      color: deviation_color(item?.quantity_deviation)?.color,
                      backgroundColor: deviation_color(item?.quantity_deviation)?.bg_color,
                    }}
                  >
                    {item.recipe_name
                      .split(" ")
                      .map((word) => word[0])
                      .join("")
                      .toUpperCase()}
                  </Avatar>
                }
                title={
                  <Typography1.Paragraph
                    ellipsis={{
                      rows: 2,
                      expandable: true,
                      symbol: "more",
                    }}
                    style={{ marginBottom: 0 }}
                  >
                    {item?.msg}
                  </Typography1.Paragraph>
                }
                description={
                  <Space direction="vertical" size={0} style={{ width: '100%' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Stack direction="vertical" size={0}>
                        <Typography1.Text type="secondary" style={{ fontSize: 12 }}>
                          {`Recipe : ${item?.recipe_name}`}
                        </Typography1.Text>
                        <Typography1.Text type="secondary" style={{ fontSize: 12 }}>
                          {`Batch : ${item?.batch_number}`}
                        </Typography1.Text>
                        <Typography1.Text type="secondary" style={{ fontSize: 12 }}>
                          {`Process : ${item?.process_name}`}
                        </Typography1.Text>
                      </Stack>
                      <Stack direction="vertical" size={0} align="end">
                        <Typography1.Text type="secondary" style={{ fontSize: 11 }}>
                          {formatDate(item?.batch_schedule_time)}
                        </Typography1.Text>
                        <Typography1.Text type="secondary" style={{ fontSize: 11 }}>
                          {formatDateTime(item?.batch_schedule_time, 'HH:mm:ss')}
                        </Typography1.Text>
                      </Stack>
                    </div>
                  </Space>
                }
              />
            </List.Item>
            {index < notificationsData.slice(0, 5).length - 1 && <Divider style={{ margin: 0 }} />}
          </React.Fragment>
        )}
      />
      <Divider style={{ margin: 0 }} />
      <div
        style={{
          textAlign: 'center',
          padding: '12px',
          cursor: 'pointer'
        }}
        onClick={() => {
          navigate("/messages");
          setOpen(false);
        }}
      >
        <Typography1.Text strong style={{ color: token.colorPrimary }}>
          View All
        </Typography1.Text>
      </div>
    </div>
  );

  // Dark mode aware colors
  const bgColor = isDark
    ? (open ? defaultColors.grey[600] : defaultColors.grey[700])
    : (open ? defaultColors.grey[300] : defaultColors.grey[100]);
  const iconColor = isDark ? defaultColors.grey[100] : defaultColors.grey[700];

  return (
    <Dropdown
      open={open}
      onOpenChange={setOpen}
      popupRender={() => dropdownContent}
      trigger={['click']}
      placement="bottomRight"
    >
      <Tooltip title="Schedule Notification" zIndex={9999}>
        <Button
          type="text"
          shape="circle"
          icon={
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Badge count={notificationsData?.length} size="small">
                <EmailOutlinedIcon style={{ fontSize: 23, display: 'block', color: iconColor }} />
              </Badge>
            </div>
          }
          style={{
            backgroundColor: bgColor,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 0,
          }}
          ref={anchorRef}
          onClick={handleToggle}
        />
      </Tooltip>
    </Dropdown>
  );
};

export default ScheduleNotification;
